import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen, CreditCard, Download, FileText, RefreshCcw, Search, Users } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import { getRequest, API_BASE_URL } from "../../../../api";
import "./PaieCyclePages.css";

const userName = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

// Fix: format numbers manually to avoid locale encoding issues in jsPDF (slash instead of space)
const money = (value) => {
    const n = Number(value || 0);
    const fixed = n.toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${intFormatted},${decPart}`;
};

const amount = (value) => Number(value || 0);
const monthLabel = (periode) => periode ? `${String(periode.mois).padStart(2, "0")}/${periode.annee}` : "-";
const safeFile = (value) => String(value || "document").replace(/[^\w-]+/g, "_");

// Clé numerique triable pour une periode (annee * 12 + mois) afin de comparer/ordonner un intervalle
const periodeKey = (periode) => periode ? Number(periode.annee) * 12 + Number(periode.mois) : null;

const PRIMARY = [32, 67, 133];    // #204385
const ACCENT  = [238, 100, 95];   // #334785

const numberToWordsFR = (value) => {
    const units = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf"];
    const teens = ["dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];
    const tens = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante-dix", "quatre-vingt", "quatre-vingt-dix"];
    const parseUnderThousand = (num) => {
        let text = "";
        const hundred = Math.floor(num / 100);
        const rest = num % 100;
        if (hundred) text += `${hundred === 1 ? "" : `${units[hundred]} `}cent${hundred > 1 && rest === 0 ? "s" : ""} `;
        if (rest < 10) text += units[rest];
        else if (rest < 20) text += teens[rest - 10];
        else {
            const ten = Math.floor(rest / 10);
            const unit = rest % 10;
            text += tens[ten] + (unit === 1 ? "-et-un" : unit > 1 ? `-${units[unit]}` : "");
        }
        return text.trim();
    };
    let n = Math.floor(amount(value));
    if (!n) return "zero Dirhams";
    let text = "";
    if (n >= 1000) {
        const thousand = Math.floor(n / 1000);
        text += `${thousand === 1 ? "" : `${parseUnderThousand(thousand)} `}mille `;
        n %= 1000;
    }
    if (n) text += parseUnderThousand(n);
    return `${text.trim()} Dirhams`;
};

const addCompanyHeader = (doc, title, periodLabel) => {
    // Title bar
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, 210, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(title, 105, 7, { align: "center" });
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Raison sociale :", 118, 20);
    doc.setFont("helvetica", "normal");
    doc.text("BLUE SCRATCH CONSULTING", 145, 20);
    doc.setFont("helvetica", "bold");
    doc.text("Affiliation CNSS :", 118, 27);
    doc.setFont("helvetica", "normal");
    doc.text("2032565", 145, 27);
    doc.setFont("helvetica", "bold");
    doc.text("Adresse :", 118, 34);
    doc.setFont("helvetica", "normal");
    doc.text("Casablanca, Maroc", 145, 34);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`Période : ${periodLabel}`, 12, 20);
};

const addPayslipPage = (doc, item, periodLabel) => {
    const { bulletin: b, user: u, lines } = item;
    addCompanyHeader(doc, "BULLETIN DE PAIE", periodLabel);

    autoTable(doc, {
        startY: 40,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.4, lineColor: 180, lineWidth: 0.1 },
        headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
        head: [["Matricule", "Nom et Prenom", "N° CNSS", "CIN", "Date de naissance", "Déduction", "Date d'embauche"]],
        body: [[u?.employeeCode || "-", userName(u), u?.numCnss || "-", u?.cin || "-", u?.dateOfBirth || "-", "0 / 0", u?.hireDate || "-"]],
    });

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.2, lineColor: 180, lineWidth: 0.1 },
        headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
        head: [["Adresse", "Fonction", "CIMR"]],
        body: [[u?.adresse || "-", u?.jobName || "-", u?.numRetraite || "-"]],
    });

    const salaryRows = lines.map((line) => {
        const isPatronal = line.type === "SOUS_TOTAL" && String(line.rubriqueCode || "").startsWith("COT_PAT_");
        const isRetenue  = line.type === "RETENUE";
        const isGain     = line.type === "GAIN";
        const isBaseSalary = line.rubriqueCode === "SALAIRE_BASE";
        return [
            line.libelle || line.rubriqueCode || "-",                                              // Libellé
            line.baseCalcul != null ? money(line.baseCalcul) : "",                                 // Base en DH
            isBaseSalary ? money(b.nombreJoursTravailles) : line.tauxApplique != null ? money(amount(line.tauxApplique) * 100) : "",  // Taux
            isBaseSalary ? "J" : line.tauxApplique != null ? "%" : "",                            // unité
            isGain     ? money(line.montant) : "",                                                 // Gain
            isRetenue  ? money(line.montant) : "",                                                 // Retenue
            isPatronal ? money(line.montant) : "",                                                 // P. patronale
        ];
    });

// Lignes spéciales — toujours 7 colonnes
    salaryRows.splice(1, 0, ["Salaire Brut imposable",  money(b.salaireBrutImposable), "", "", "", "", ""]);
    salaryRows.push(       ["Salaire brut global",       money(b.salaireBrutGlobal),   "", "", "", "", ""]);
    salaryRows.push(       ["Gain Net",                  money(b.gainNet),             "", "", "", "", ""]);
    salaryRows.push(       ["Arrondis",                  "",                           "", "", money(b.arrondi), "", ""]);

    const totalGains = lines.filter((line) => line.type === "GAIN").reduce((sum, line) => sum + amount(line.montant), 0);
    const totalRetenues = lines.filter((line) => line.type === "RETENUE").reduce((sum, line) => sum + amount(line.montant), 0);
    const totalPatronales = lines
        .filter((line) => line.type === "SOUS_TOTAL" && String(line.rubriqueCode || "").startsWith("COT_PAT_"))
        .reduce((sum, line) => sum + amount(line.montant), 0);

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 3,
        margin: { left: 12, right: 12 },
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.4, lineColor: 180, lineWidth: 0.12 },
        headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
        columnStyles: {
            0: { cellWidth: 56 },                    // Libelle : 62 → 56
            1: { cellWidth: 24, halign: "right" },   // Base en DH
            2: { cellWidth: 18, halign: "right" },   // Taux/nombre
            3: { cellWidth: 14, halign: "center" },  // %/J  : 8 → 14
            4: { cellWidth: 30, halign: "right" },   // Gain
            5: { cellWidth: 24, halign: "right" },   // Retenue
            6: { cellWidth: 24, halign: "right" },   // P. patronale
        },
        head: [["Libellé", "Base en DH", "Taux / nombre", "", "Gain", "Retenue", "P. patronale"]],
        body: salaryRows,
        foot: [["", "", "", "Totaux :", money(totalGains), money(totalRetenues), money(totalPatronales)]],
        footStyles: { fillColor: [240, 244, 252], textColor: 0, fontStyle: "bold" },
    });

    const y = doc.lastAutoTable.finalY + 8;
    doc.setFillColor(...PRIMARY);
    doc.rect(12, y, 142, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Net à payer", 15, y + 6);
    doc.text(numberToWordsFR(b.netAPayer), 42, y + 6);
    doc.setFillColor(...ACCENT);
    doc.rect(154, y, 44, 10, "F");
    doc.text(`${money(b.netAPayer)} MAD`, 176, y + 6, { align: "center" });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.text(`Mode de paiement : ${b.modePaiement || "VIREMENT"}`, 12, y + 18);
    doc.setTextColor(...ACCENT);
    doc.setTextColor(0, 0, 0);
};

const PaieDocumentsPage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [activeTab, setActiveTab] = useState("bulletins");
    // Intervalle de periodes : periode de debut -> periode de fin (remplace la selection unique)
    const [periodeDebutId, setPeriodeDebutId] = useState("");
    const [periodeFinId, setPeriodeFinId] = useState("");
    const [q, setQ] = useState("");
    const [selectedUserIds, setSelectedUserIds] = useState({});

    useEffect(() => {
        dispatch(getAllUsersRequest());
        ["periodes", "bulletins", "remunerations"].forEach((resource) => dispatch(payrollFetchResourceRequest(resource)));
    }, [dispatch]);

    // Periodes triees chronologiquement (utile pour le select et pour deduire l'intervalle)
    const sortedPeriodes = useMemo(
        () => [...(payroll.periodes || [])].sort((a, b) => periodeKey(a) - periodeKey(b)),
        [payroll.periodes]
    );

    useEffect(() => {
        if (!periodeDebutId && !periodeFinId && sortedPeriodes.length > 0) {
            const lastId = String(sortedPeriodes[sortedPeriodes.length - 1].id);
            setPeriodeDebutId(lastId);
            setPeriodeFinId(lastId);
        }
    }, [sortedPeriodes, periodeDebutId, periodeFinId]);

    const periodeDebut = useMemo(() => sortedPeriodes.find((p) => String(p.id) === String(periodeDebutId)), [sortedPeriodes, periodeDebutId]);
    const periodeFin = useMemo(() => sortedPeriodes.find((p) => String(p.id) === String(periodeFinId)), [sortedPeriodes, periodeFinId]);

    // Bornes numeriques de l'intervalle (gere le cas ou debut/fin sont inverses)
    const intervalBounds = useMemo(() => {
        const debutKey = periodeKey(periodeDebut);
        const finKey = periodeKey(periodeFin);
        if (debutKey == null && finKey == null) return null;
        if (debutKey == null) return { min: finKey, max: finKey };
        if (finKey == null) return { min: debutKey, max: debutKey };
        return { min: Math.min(debutKey, finKey), max: Math.max(debutKey, finKey) };
    }, [periodeDebut, periodeFin]);

    // Toutes les periodes comprises dans l'intervalle selectionne
    const selectedPeriodes = useMemo(() => {
        if (!intervalBounds) return sortedPeriodes;
        return sortedPeriodes.filter((p) => {
            const key = periodeKey(p);
            return key >= intervalBounds.min && key <= intervalBounds.max;
        });
    }, [sortedPeriodes, intervalBounds]);

    const selectedPeriodeIds = useMemo(() => new Set(selectedPeriodes.map((p) => String(p.id))), [selectedPeriodes]);

    // Libelle d'intervalle pour les PDF (ex: "01/2026" ou "01/2026 - 03/2026")
    const intervalLabel = useMemo(() => {
        if (selectedPeriodes.length === 0) return "-";
        if (selectedPeriodes.length === 1) return monthLabel(selectedPeriodes[0]);
        return `${monthLabel(selectedPeriodes[0])} - ${monthLabel(selectedPeriodes[selectedPeriodes.length - 1])}`;
    }, [selectedPeriodes]);

    const bulletins = useMemo(
        () => (payroll.bulletins || []).filter((b) => selectedPeriodeIds.size === 0 || selectedPeriodeIds.has(String(b.periodeId))),
        [payroll.bulletins, selectedPeriodeIds]
    );
    const remunerationByUser = useMemo(() => {
        const map = {};
        (payroll.remunerations || []).forEach((rem) => {
            if (!map[rem.userId]) map[rem.userId] = rem;
        });
        return map;
    }, [payroll.remunerations]);

    const filteredBulletins = useMemo(() => {
        return bulletins.filter((b) => {
            const user = users.find((u) => u.id === b.userId);
            const nameMatch = !q.trim() || userName(user).toLowerCase().includes(q.toLowerCase());
            return nameMatch;
        });
    }, [bulletins, users, q]);

    const selectedBulletins = () => filteredBulletins.filter((b) => selectedUserIds[b.userId]);
    const token = () => sessionStorage.getItem("token") || localStorage.getItem("token");

    const loadDetails = async (targets) => {
        const detailed = [];
        for (const bulletin of targets) {
            const response = await getRequest({ url: `${API_BASE_URL}/api/lignes-bulletins-paie?bulletinId=${bulletin.id}`, token: token() });
            const periode = sortedPeriodes.find((p) => String(p.id) === String(bulletin.periodeId));
            detailed.push({
                bulletin,
                user: users.find((u) => u.id === bulletin.userId),
                lines: response.data || [],
                periodLabel: monthLabel(periode),
            });
        }
        return detailed;
    };

    const downloadPayslips = async (singleBulletin = null) => {
        const targets = singleBulletin ? [singleBulletin] : selectedBulletins();
        if (!targets.length) {
            Swal.fire({ icon: "warning", text: "Selectionnez au moins un bulletin." });
            return;
        }
        Swal.fire({ title: "Generation PDF...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        try {
            const detailed = await loadDetails(targets);
            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            detailed.forEach((item, index) => {
                if (index > 0) doc.addPage();
                addPayslipPage(doc, item, item.periodLabel);
            });
            const fileName = singleBulletin
                ? `Bulletin_${safeFile(userName(detailed[0].user))}_${safeFile(detailed[0].periodLabel)}.pdf`
                : `Bulletins_Paie_${safeFile(intervalLabel)}.pdf`;
            doc.save(fileName);
            Swal.close();
        } catch (error) {
            Swal.fire({ icon: "error", text: "Impossible de generer le PDF du bulletin." });
        }
    };

    const downloadJournal = () => {
        if (!filteredBulletins.length) {
            Swal.fire({ icon: "warning", text: "Aucun bulletin disponible." });
            return;
        }
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        // Header bar
        doc.setFillColor(...PRIMARY);
        doc.rect(0, 0, 297, 10, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`Journal de paie - ${intervalLabel}`, 148, 7, { align: "center" });
        doc.setTextColor(0, 0, 0);

        const rows = filteredBulletins.map((b) => {
            const u = users.find((user) => user.id === b.userId);
            return [
                u?.employeeCode || b.employeeCode || "-",
                userName(u),
                money(b.salaireBase),
                money(b.nombreJoursTravailles),
                money(b.montantHeuresSupplementaires),
                money(b.salaireBrutImposable),
                money(b.fraisProfessionnels),
                money(b.totalCotisationsSalariales),
                money(b.ir),
                money(b.totalCotisationsPatronales),
                money(b.netAPayer),
                money(b.arrondi),
            ];
        });
        const totals = filteredBulletins.reduce((acc, b) => ({
            salaireBase: acc.salaireBase + amount(b.salaireBase),
            jours: acc.jours + amount(b.nombreJoursTravailles),
            hs: acc.hs + amount(b.montantHeuresSupplementaires),
            brut: acc.brut + amount(b.salaireBrutImposable),
            frais: acc.frais + amount(b.fraisProfessionnels),
            cotisations: acc.cotisations + amount(b.totalCotisationsSalariales),
            ir: acc.ir + amount(b.ir),
            patronal: acc.patronal + amount(b.totalCotisationsPatronales),
            net: acc.net + amount(b.netAPayer),
            arrondi: acc.arrondi + amount(b.arrondi),
        }), { salaireBase: 0, jours: 0, hs: 0, brut: 0, frais: 0, cotisations: 0, ir: 0, patronal: 0, net: 0, arrondi: 0 });
        autoTable(doc, {
            startY: 14,
            margin: { left: 8, right: 8 },   // ← ajouter cette ligne
            theme: "grid",
            styles: { fontSize: 6, cellPadding: 1.2 },
            headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
            head: [["Matricule", "Nom & Prénom", "Salaire de base", "Jours", "Montant HS", "Brut imposable", "Frais prof.", "Cotisation sal.", "Retenue IR", "Cotisation PP", "Net à payer", "Arrondi"]],
            body: rows,
            foot: [["", "Total général", money(totals.salaireBase), money(totals.jours), money(totals.hs), money(totals.brut), money(totals.frais), money(totals.cotisations), money(totals.ir), money(totals.patronal), money(totals.net), money(totals.arrondi)]],
            footStyles: { fillColor: [240, 244, 252], textColor: 0, fontStyle: "bold" },
        });
        doc.save(`Journal_Paie_${safeFile(intervalLabel)}.pdf`);
    };

    const downloadVirements = () => {
        if (!filteredBulletins.length) {
            Swal.fire({ icon: "warning", text: "Aucun virement a exporter." });
            return;
        }

        const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const today = new Date();
        const dateStr = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;

        // ── En-tête : date alignée à droite ──────────────────────────────────────
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Casablanca, le : ${dateStr}`, 198, 20, { align: "right" });

        // ── Destinataire ─────────────────────────────────────────────────────────
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("À l\u2019intention de l\u2019agence Attijariwafa Bank.", 198, 38, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("Boulevard Al Qods, Casablanca", 198, 44, { align: "right" });

        // ── Objet ────────────────────────────────────────────────────────────────
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Objet : ", 12, 60);
        doc.setFont("helvetica", "normal");
        doc.text("Ordre de virement", 28, 60);

        // ── Formule de politesse ─────────────────────────────────────────────────
        doc.setFontSize(10);
        doc.text("Madame, Monsieur,", 12, 72);

        // ── Corps : numéro de compte en gras ─────────────────────────────────────
        const accountNumber = "007 532 63520002 41856987 04";
        const bodyLine = "Par le d\u00e9bit de mon compte n\u00b0 ";
        doc.setFont("helvetica", "normal");
        doc.text(bodyLine, 12, 82);
        const bodyLineWidth = doc.getTextWidth(bodyLine);
        doc.setFont("helvetica", "bold");
        doc.text(accountNumber, 12 + bodyLineWidth, 82);

        doc.setFont("helvetica", "normal");
        doc.text("Je vous prie de bien vouloir effectuer les virements suivants :", 12, 92);

        // ── Tableau ───────────────────────────────────────────────────────────────
        const rows = filteredBulletins.map((b, idx) => {
            const u = users.find((user) => user.id === b.userId);
            const rem = remunerationByUser[b.userId] || {};
            return [
                idx + 1,
                money(b.netAPayer),
                userName(u),
                rem.rib || "-",
                [rem.banque].filter(Boolean).join(" / ") || "-",
            ];
        });

        const total = filteredBulletins.reduce((sum, b) => sum + amount(b.netAPayer), 0);

        autoTable(doc, {
            startY: 98,
            margin: { left: 12, right: 12 },
            theme: "grid",
            styles: {
                fontSize: 9,
                cellPadding: 3,
                lineColor: [0, 0, 0],      // noir au lieu de [180, 180, 180]
                lineWidth: 0.3,             // un peu plus épais
                textColor: [30, 30, 30],
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: "bold",
                halign: "center",
                lineColor: [0, 0, 0],      // noir
                lineWidth: 0.3,
            },
            columnStyles: {
                0: { cellWidth: 14, halign: "center" },   // Seq
                1: { cellWidth: 32, halign: "right" },    // Montant
                2: { cellWidth: 42, halign: "left" },     // Bénéficiaire (réduit de 52 → 42)
                3: { cellWidth: 52, halign: "left" },     // RIB
                4: { cellWidth: 36, halign: "left" },     // Banque
            },
            head: [["Seq.", "Montant (MAD)", "B\u00e9n\u00e9ficiaire", "RIB", "Banque"]],
            body: rows
        });

        // ── Montant en lettres ────────────────────────────────────────────────────
        const afterTable = doc.lastAutoTable.finalY + 10;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);

        // ── Signature ─────────────────────────────────────────────────────────────
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("Signature / Cachet", 150, afterTable + 30);

        doc.save(`Ordre_Virement_${safeFile(intervalLabel)}.pdf`);
    };
    const selectAll = () => {
        const shouldSelect = filteredBulletins.some((b) => !selectedUserIds[b.userId]);
        const next = {};
        if (shouldSelect) filteredBulletins.forEach((b) => { next[b.userId] = true; });
        setSelectedUserIds(next);
    };

    // UI color vars
    const btnPrimary = { background: "#204385", color: "#fff", border: "none" };
    const btnAccent  = { background: "#334785", color: "#fff", border: "none" };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><FileText size={18} />Documents de Paie</h2>
                    <p>Téléchargement PDF des bulletins, journal de paie et ordre de virement.</p>
                </div>
                <button className="pcyc-btn" style={btnPrimary} type="button" onClick={() => ["bulletins", "remunerations"].forEach((r) => dispatch(payrollFetchResourceRequest(r)))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>

            <div className="pcyc-toolbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ minWidth: "180px" }}>
                        <span>Période de</span>
                        <select value={periodeDebutId} onChange={(e) => setPeriodeDebutId(e.target.value)}>
                            <option value="">Sélectionner</option>
                            {sortedPeriodes.map((p) => <option key={p.id} value={p.id}>{monthLabel(p)}</option>)}
                        </select>
                    </label>
                    <label style={{ minWidth: "180px" }}>
                        <span>à la période</span>
                        <select value={periodeFinId} onChange={(e) => setPeriodeFinId(e.target.value)}>
                            <option value="">Sélectionner</option>
                            {sortedPeriodes.map((p) => <option key={p.id} value={p.id}>{monthLabel(p)}</option>)}
                        </select>
                    </label>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    {activeTab === "bulletins" && <button className="pcyc-btn" style={btnAccent} type="button" onClick={() => downloadPayslips()}><Download size={15} /> Imprimer les bulletins de paie sélectionnés</button>}
                    {activeTab === "virement" && <button className="pcyc-btn" style={btnAccent} type="button" onClick={downloadVirements}><Download size={15} /> Ordre de virement</button>}
                    {activeTab === "journal" && <button className="pcyc-btn" style={btnAccent} type="button" onClick={downloadJournal}><Download size={15} /> Journal de paie</button>}
                </div>
            </div>

            <div className="pcyc-toolbar" style={{ borderBottom: "none", borderTopLeftRadius: "10px", borderTopRightRadius: "10px", margin: "14px 18px 0", background: "#f8fafc" }}>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    <button className="pcyc-btn" style={activeTab === "bulletins" ? btnPrimary : {}} onClick={() => setActiveTab("bulletins")}><Users size={14} /> Bulletins de paie ({filteredBulletins.length})</button>
                    <button className="pcyc-btn" style={activeTab === "virement" ? btnPrimary : {}} onClick={() => setActiveTab("virement")}><CreditCard size={14} /> Ordre de virement</button>
                    <button className="pcyc-btn" style={activeTab === "journal" ? btnPrimary : {}} onClick={() => setActiveTab("journal")}><BookOpen size={14} /> Journal de paie</button>
                </div>
                <div className="pcyc-search" style={{ minWidth: "220px", marginLeft: "auto" }}>
                    <Search size={14} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher collaborateur..." style={{ padding: "6px 10px 6px 30px" }} />
                </div>
            </div>

            <div className="pcyc-table-card" style={{ marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                {activeTab === "bulletins" && (
                    <table className="pcyc-table">
                        <thead>
                        <tr>
                            <th><input type="checkbox" checked={filteredBulletins.length > 0 && !filteredBulletins.some((b) => !selectedUserIds[b.userId])} onChange={selectAll} /></th>
                            <th>Matricule</th><th>Collaborateur</th><th>Brut</th><th>Retenues</th><th>Net à payer</th><th>Statut</th><th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredBulletins.length === 0
                            ? <tr><td colSpan="8" className="pcyc-empty">Aucun bulletin de paie disponible</td></tr>
                            : filteredBulletins.map((b) => {
                                const u = users.find((user) => user.id === b.userId);
                                return (
                                    <tr key={b.id}>
                                        <td><input type="checkbox" checked={!!selectedUserIds[b.userId]} onChange={() => setSelectedUserIds((prev) => ({ ...prev, [b.userId]: !prev[b.userId] }))} /></td>
                                        <td><span className="pcyc-code">{u?.employeeCode || b.employeeCode || "N/A"}</span></td>
                                        <td><strong>{userName(u)}</strong></td>
                                        <td>{money(b.salaireBrutImposable)} MAD</td>
                                        <td>{money(b.totalRetenues)} MAD</td>
                                        <td><strong>{money(b.netAPayer)} MAD</strong></td>
                                        <td><span className={`pcyc-status ${b.statut}`}>{b.statut}</span></td>
                                        <td className="pcyc-row-actions">
                                            <button type="button" onClick={() => downloadPayslips(b)} title="Telecharger PDF"><Download size={14} /> PDF</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {activeTab === "virement" && (
                    <table className="pcyc-table">
                        <thead>
                        <tr><th>Matricule</th><th>Beneficiaire</th><th>RIB</th><th>Banque / Agence</th><th>Montant Net</th><th>Statut</th></tr>
                        </thead>
                        <tbody>
                        {filteredBulletins.length === 0
                            ? <tr><td colSpan="6" className="pcyc-empty">Aucun virement pour cette periode</td></tr>
                            : filteredBulletins.map((b) => {
                                const u = users.find((user) => user.id === b.userId);
                                const rem = remunerationByUser[b.userId] || {};
                                return (
                                    <tr key={b.id}>
                                        <td><span className="pcyc-code">{u?.employeeCode || b.employeeCode || "N/A"}</span></td>
                                        <td><strong>{userName(u)}</strong></td>
                                        <td><span style={{ fontFamily: "monospace", fontSize: "11px" }}>{rem.rib || "N/A"}</span></td>
                                        <td>{[rem.banque].filter(Boolean).join(" - ") || "N/A"}</td>
                                        <td><strong>{money(b.netAPayer)} MAD</strong></td>
                                        <td>{rem.rib
                                            ? <span style={{ color: "#204385", fontSize: "11px", fontWeight: 700 }}>Prêt pour virement</span>
                                            : <span style={{ color: "#334785", fontSize: "11px", fontWeight: 700 }}>RIB manquant</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {activeTab === "journal" && (
                    <table className="pcyc-table">
                        <thead>
                        <tr><th>Matricule</th><th>Collaborateur</th><th>Salaire Base</th><th>Jours</th><th>Brut Imposable</th><th>Cotis. Sal.</th><th>IR</th><th>Cotis. PP</th><th>Net à payer</th></tr>
                        </thead>
                        <tbody>
                        {filteredBulletins.length === 0
                            ? <tr><td colSpan="9" className="pcyc-empty">Aucun bulletin calcule pour cette periode</td></tr>
                            : filteredBulletins.map((b) => {
                                const u = users.find((user) => user.id === b.userId);
                                return (
                                    <tr key={b.id}>
                                        <td><span className="pcyc-code">{u?.employeeCode || b.employeeCode || "N/A"}</span></td>
                                        <td><strong>{userName(u)}</strong></td>
                                        <td>{money(b.salaireBase)}</td>
                                        <td>{money(b.nombreJoursTravailles)}</td>
                                        <td>{money(b.salaireBrutImposable)}</td>
                                        <td>{money(b.totalCotisationsSalariales)}</td>
                                        <td>{money(b.ir)}</td>
                                        <td>{money(b.totalCotisationsPatronales)}</td>
                                        <td><strong>{money(b.netAPayer)}</strong></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default PaieDocumentsPage;